
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Spinner } from "@/components/ui/spinner";

interface AdminCheckProps {
  children: React.ReactNode;
}

export const AdminCheck = ({ children }: AdminCheckProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          toast({
            variant: "destructive",
            title: "Authentication Error",
            description: "Please sign in again.",
          });
          await supabase.auth.signOut();
          navigate("/auth");
          return;
        }

        if (!session) {
          console.log("No valid session found");
          navigate("/auth");
          return;
        }

        console.log("Checking admin status for user:", session.user.id);

        // Check if user is admin OR producer
        const { data: userRole, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .in('role', ['admin', 'producer'])
          .single();

        if (roleError) {
          console.error("Error checking admin status:", roleError);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to verify admin status. Please try again.",
          });
          navigate("/dashboard");
          return;
        }

        const hasAdminAccess = !!userRole;
        console.log("Admin check result:", hasAdminAccess, "Role:", userRole?.role);

        if (!hasAdminAccess) {
          console.log("User is not an admin or producer");
          toast({
            variant: "destructive",
            title: "Access Denied",
            description: "You don't have permission to access this page.",
          });
          navigate("/dashboard");
          return;
        }

        console.log("Admin access granted");
        setHasAccess(true);
      } catch (error) {
        console.error("Error in checkAdminStatus:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
        });
        navigate("/dashboard");
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate, toast]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return hasAccess ? <>{children}</> : null;
};
